// components/AuthBanner.jsx
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useAuth } from "../contexts/AuthContext";
import { Separator } from "./ui/separator";
import { AlertCircle, Check } from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { useNavigate } from "react-router-dom";
import { useToast } from "./ui/use-toast";

export const AuthBanner = ({ hideCard, externalDialog, onOpenChange }) => {
  const navigate = useNavigate();
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const { signIn, signUp, signOut, signInWithGoogle, resetPassword } = useAuth();
  const { toast } = useToast();

  // Use either external or internal dialog state
  const dialogOpen = externalDialog !== undefined ? externalDialog : showAuthDialog;
  const handleDialogChange = onOpenChange || setShowAuthDialog;

  const handleSuccess = () => {
    handleDialogChange(false);
    navigate('/profile');
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setError(null);
  
    // Password validation for signup
    if (!isLogin) {
      if (password !== confirmPassword) {
        setError("Passwords don't match");
        return;
      }
  
      if (password.length < 6) {
        setError("Password must be at least 6 characters");
        return;
      }
    }
  
    try {
      const result = isLogin
        ? await signIn(email, password)
        : await signUp(email, password);
  
      if (result.error) throw result.error;
      
      // Check if this is a new signup that needs verification
      if (!isLogin && result.isNewSignUp) {
        // Close the dialog
        handleDialogChange(false);
        
        // Store verification info in localStorage
        localStorage.setItem('verificationNeeded', 'true');
        localStorage.setItem('verificationEmail', email);
        localStorage.setItem('isNewSignUp', 'true');
        localStorage.setItem('verificationTimestamp', Date.now().toString());
        
        // Show a toast notification
        toast({
          title: 'Account Created',
          description: 'Please check your email to verify your account.',
          duration: 10000
        });
        
        return; // Stop execution here - don't redirect
      }
      
      // For successful logins or verified signups
      handleSuccess();
    } catch (error) {
      setError(error.message || 'An error occurred during authentication.');
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError(null);
    setResetSuccess(false);

    try {
      const { error } = await resetPassword(email);
      if (error) throw error;
      setResetSuccess(true);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const { error } = await signInWithGoogle();
      if (error) throw error;
      handleSuccess();
    } catch (error) {
      setError(error.message);
    }
  };

  const resetDialog = () => {
    setIsLogin(true);
    setIsForgotPassword(false);
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setError(null);
    setResetSuccess(false);
  };

  return (
    <>
      {!hideCard && (
        <Card className="bg-primary/5 mb-6">
          <CardContent className="flex items-center justify-between p-6">
            <p className="text-sm">
              Create an account to save your preferences and track your progress over time!
            </p>
            <Button onClick={() => handleDialogChange(true)}>
              Sign Up / Login
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog 
        open={dialogOpen} 
        onOpenChange={(open) => {
          handleDialogChange(open);
          if (!open) resetDialog();
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {isForgotPassword 
                ? "Reset Password"
                : isLogin 
                  ? "Login" 
                  : "Create Account"}
            </DialogTitle>
            <DialogDescription>
              {isForgotPassword
                ? "Enter your email address and we'll send you a link to reset your password."
                : isLogin
                  ? "Welcome back! Sign in to access your saved preferences."
                  : "Create an account to save your preferences and track progress."}
            </DialogDescription>
          </DialogHeader>
          
          {isForgotPassword ? (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {resetSuccess && (
                <Alert>
                  <Check className="h-4 w-4" />
                  <AlertDescription>
                    Password reset email sent! Check your inbox for further instructions.
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex flex-col gap-2">
                <Button type="submit" className="w-full">
                  Send Reset Link
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsForgotPassword(false)}
                >
                  Back to Login
                </Button>
              </div>
            </form>
          ) : (
            <div className="flex flex-col space-y-4">
              <Button 
                variant="outline" 
                onClick={handleGoogleSignIn}
                className="w-full"
              >
                <svg className="mr-2 h-4 w-4" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512">
                  <path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path>
                </svg>
                Continue with Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>

              <form onSubmit={handleAuth} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                {/* Add confirm password field for signup */}
                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                )}

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button type="submit" className="w-full">
                  {isLogin ? "Sign In" : "Create Account"}
                </Button>

                {isLogin && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setIsForgotPassword(true)}
                    className="w-full text-sm text-muted-foreground hover:text-primary"
                  >
                    Forgot password?
                  </Button>
                )}

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setIsLogin(!isLogin)}
                >
                  {isLogin
                    ? "Don't have an account? Sign Up"
                    : "Already have an account? Sign In"}
                </Button>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};